import React, { useEffect, useState, FC } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { Table, TableColumnsType, TableProps } from 'antd';

import { getCommonCodeLabel } from '../../../lib/store';
import config from '../../../lib/config';
import Button from '../../Button';
import DownloadButton from '../../DownloadButton';
import Pagination from '../../Pagination';
import { IGetStudents, IStudentItem } from '../../../models/students.model';
import qs from 'qs';
import useAxios from '../../../hooks/useAxios';
import ErrorResponse from '../../ErrorResponse';
import TransferCampusModal from './modals/TransferCampusModal';
import Loader from '../../Loader';
import { getStudents2 } from '../../../api/students.api';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import exportTable from '../../../lib/exportTable';
import moment from 'moment';
import { addHyphen } from '../../../lib/util';
import { TextBlock } from '../../../styles/components/Block/TextBlock';
import styled from 'styled-components';

interface IProps {
  memberCode: string;
  setMemberCode: (memberCode: string) => void;
  setCampusCode: (campusCode: string) => void;
}

/**
 * scroll table style
 */
const ScrollTable: FC<TableProps<any>> = styled(Table)<TableProps<any>>`
    .ant-table-body > table {
        margin-top: 0;
        border: 0;
    }
`

const StudentTable: FC<IProps> = ({
  memberCode,
  setMemberCode,
  setCampusCode,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetStudents>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetStudents>();

  const [transferCampusVisible, setTransferCampusVisible] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [params, setParams] = useState({});
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    doGetStudents();
  }, [
    query.page,
    query.statusCode,
    query.eduStepCode,
    query.field,
    query.keyword,
    query.campusCode,
  ]);

  /*
  useEffect(() => {
    if (pageNo === 1) {
      //doGetStudents();
    } else {
      setPageNo(1);
    }
  }, [currentCampus]);
  */

  useEffect(() => {
    if (query.transfer) {
      setTransferCampusVisible(true);
    }
  }, [query.transfer]);

  useEffect(() => {
    if (res2) {
      const data = _.cloneDeep(res2?.data);
      data.forEach((item: any) => {
        item.eduStepCode = getCommonCodeLabel('PR0006', item.eduStepCode, i18n.language);
        item.gradeCode = getCommonCodeLabel('SY0006', item.gradeCode, i18n.language);
        item.statusCode = getCommonCodeLabel('MB0003', item.statusCode, i18n.language);
        item.mobilePhone = addHyphen(item.mobilePhone);
        item.parentMobilePhone = addHyphen(item.parentMobilePhone);
      });
      const excelColumns = _.cloneDeep(columns);
      excelColumns.unshift({
        title: t('student.student.column64'),
        dataIndex: 'memberCode',
        key: 'memberCode',
      });
      excelColumns.push({
        title: t('common.text.address1'),
        dataIndex: 'address',
        key: 'address',
      });
      excelColumns.push({
        title: t('student.student.66'),
        dataIndex: 'mobilePhone',
        key: 'mobilePhone',
      });
      excelColumns.push({
        title: t('student.student.column9'),
        dataIndex: 'parentMobilePhone',
        key: 'parentMobilePhone',
      });

      const currentDate = moment(new Date());
      exportTable(excelColumns, data, `${t('student.student.67')}_(${currentDate.format('YYYYMMDD')})`, t('student.student.67'), [20, 20, 10, 15, 15, 10, 10, 55, 15, 15]);
    }
  }, [res2]);

  const doGetStudents = () => {
    const _params: any = {
      pageNo: 1,
      perPage: 999999999,
      campusCode: currentCampus,
    }

    if (query.statusCode) {
      _params.statusCode = String(query.statusCode);
    }
    if (query.eduStepCode) {
      _params.eduStepCode = String(query.eduStepCode);
    }
    if (query.field && query.keyword) {
      _params[String(query.field)] = String(query.keyword);
    }
    if (query.page) {
      _params.pageNo = Number(query.page);
      setPageNo(_params.pageNo);
    }

    execute(getStudents2(_params));

    _params.page = _params.pageNo;
    delete _params.pageNo;
    delete _params.perPage;
    if (query.field && query.keyword) {
      _params.field = String(query.field);
      _params.keyword = String(query.keyword);
    }

    setParams(_params);
  }

  const onExportTable = () => {
    execute2(getStudents2({ ...params, pageNo: 1, perPage: 999999999 }));
  };

  const columns: TableColumnsType<IStudentItem> = [
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      align: 'center',
      ellipsis: true,
      width: '10%',
      render: (value) => (
        <TextBlock hasEllipsis={'action'}>
          {getCommonCodeLabel(
            'PR0006',
            value || t('student.student.20'),
            i18n.language,
          )}
        </TextBlock>
      ),
    },
    {
      title: t('auth.login.label1'),
      dataIndex: 'webId',
      key: 'webId',
      width: '15%',
      ellipsis: true,
    },
    {
      title: t('student.student.placeholder2'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      width: '12%',
      ellipsis: true,
    },
    {
      title: t('student.student.column1'),
      dataIndex: 'studentEngName',
      ellipsis: true,
      width: '15%',
      key: 'studentEngName',
      render: (value, record, index) => {
        if(value) return value;
        else return '-';
      },
    },
    {
      title: t('student.student.column64'),
      dataIndex: 'memberCode',
      ellipsis: true,
      width: '13%',
      key: 'memberCode',
    },
    {
      title: t('student.student.column2'),
      dataIndex: 'schoolName',
      ellipsis: true,
      width: '15%',
      key: 'schoolName',
    },
    {
      title: t('student.student.column3'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      ellipsis: true,
      width: '10%',
      // SY0006 = 학년
      render: (value: string) => (
        <TextBlock hasEllipsis={'action'}>
          {getCommonCodeLabel('SY0006', value, i18n.language)}
        </TextBlock>
      ),
    },
    {
      title: t('student.student.column4'),
      dataIndex: 'statusCode',
      key: 'statusCode',
      ellipsis: true,
      width: '10%',
      // MB0003 = 학생 상태
      render: (value: string) => (
        <TextBlock hasEllipsis={'action'}>
          {getCommonCodeLabel('MB0003', value, i18n.language)}
        </TextBlock>
      ),
    },
  ];

  return (
    <div>
      {isLoading || isLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />

      <div className="comm-bx__between">
        <div className="comm-sch_result">
          {t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}
        </div>
      </div>
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={res?.data}
        pagination={false}
        onRow={(record) => {
          return {
            onClick: () => {
              // setMemberCode(record.memberCode);
              // setCampusCode(record.campusCode);
              if (memberCode !== record.memberCode) {
                history.push(`/student/student?${qs.stringify({
                  ...params,
                  memberCode: record.memberCode,
                  campusCode: record.campusCode,
                })}`);
              }
            },
          };
        }}
        rowClassName={(record: any) => record.memberCode === memberCode ? 'chosen' : 'pointer'}
      />
      <div className="comm-bx__space">
        <div className="comm-btn__area">
            <DownloadButton onClick={onExportTable} />
            <Button
              className="btn-basic pink-line"
              onClick={() => setTransferCampusVisible(true)}
              text={t('student.student.button1')}
            />
          {/*<div className="w-100">*/}
          {/*  <Pagination*/}
          {/*    count={res?.total || 0}*/}
          {/*    page={pageNo}*/}
          {/*    param={params}*/}
          {/*    path="/student/student"*/}
          {/*    small*/}
          {/*  />*/}
          {/*</div>*/}
        </div>
      </div>

      {transferCampusVisible && (
        <TransferCampusModal
          visible={transferCampusVisible}
          setVisible={setTransferCampusVisible}
          doGetAllStudents={doGetStudents}
          setMemberCode={setMemberCode}
          setCampusCode={setCampusCode}
        />
      )}
    </div>
  );
};

export default StudentTable;
